import { defineStore } from "pinia";
import { StoreModule } from "@/constants/store.ts";
import { GlobalPolicySection } from "@/constants/msp.ts";
import api from "@/_helpers/api.ts";
import { axiosInstance } from "@/plugins/https.ts";
import type { CloudAppsSettings } from "@/_store/cloud-apps/cloud-apps-settings.module.ts";
import type {
  DevicesSettings,
  NgavSettings,
  WifiPhishingSettings,
} from "@/_store/devices-settings.module.ts";
import type { AxiosResponse } from "axios";
import type { PrivacySensitiveDataSettings } from "@/_store/endpoint-data-governance/privacy-sensitive-data.module.ts";
import type {
  UserDataGovernanceExclusions,
  UserDataGovernanceSettings,
} from "@/_store/user-data-governance/user-data-governance.module.ts";
import type {
  AutoForwardingBlockingPolicyConfig,
  BlockedEmailReportsConfig,
  EmailSecuritySettings,
} from "@/_store/email-security/email-settings.module.ts";
import type { EdrSettings } from "@/_store/edr-settings.module.ts";
import type {
  DevicePostureFormData,
  DevicePostureRegularItem,
  DevicePostureUsbLockdownItem,
  DevicePostureUserAccountItem,
  DevicePostureWifiItem,
} from "@/_store/device-posture/devices-posture-settings.module.ts";
import type { DevicePostureOsType } from "@/constants/devices.ts";
import { DeviceVulnerability } from "@/constants/devices.ts";
import type { Paged, Pagination } from "@/types.ts";
import type { WorkspaceType } from "@/constants/workplaces.ts";
import type { MspLabel } from "@/_store/msp/msp-labels.module.ts";
import { SnackbarTypes, useSnackbarStore } from "@/_store/snackbar.module.ts";
import { i18n } from "@/plugins/i18n.ts";
import { FilterContext, useFiltersStore } from "@/_store/filters.module.ts";
import {
  denormalizeGlobalPolicyResponse,
  normalizeDevicesSettings,
  normalizeEmailSecuritySettings,
  normalizeEndpointSecurityAddonsSettings,
  normalizeEndpointSecurityNgavSettings,
  normalizePostureSettings,
} from "@/_store/msp/global-policies/adapters.ts";
import type { MalwareScanItem } from "@/_store/endpoint-security/malware-scan.module.ts";

export interface GlobalPolicyListItem {
  modifiedAt: number;
  name: string;
  assignedWorkspaceCount: number;
  description: string;
  sections: GlobalPolicySection[];
  id?: string;
  workspaceId?: string;
}

interface GlobalPoliciesState {
  globalPolicies: Paged<GlobalPolicyListItem>;
  pagination: Pagination;
  loading: boolean;
}

export interface GeneralInfoSection {
  name: string | null;
  description: string | null;
}

export interface UserDataGovernanceGlobalSettings {
  monitoring: UserDataGovernanceSettings;
  exclusions: UserDataGovernanceExclusions;
}

export interface EndpointSecurityGlobalSettings {
  addons: WifiPhishingSettings;
  devicePosture: EndpointSecurityGlobalPostureSettings;
  ngav: NgavSettings;
}

export interface GlobalPolicyDevicePostureRegularItem extends DevicePostureRegularItem {
  temporaryId?: string;
}

export interface GlobalPolicyDevicePostureUserAccountItem extends DevicePostureUserAccountItem {
  temporaryId?: string;
}

export interface GlobalPolicyDevicePostureWifiItem extends DevicePostureWifiItem {
  temporaryId?: string;
}

export interface GlobalPolicyDevicePostureUsbLockdownItem extends DevicePostureUsbLockdownItem {
  temporaryId?: string;
}

export type GlobalPoliciesDevicePostureFormData = DevicePostureFormData & {
  temporaryId?: string;
};

export type GlobalPolicyRegularDevicePostureSettings = {
  [osType in DevicePostureOsType]: Record<
    DeviceVulnerability,
    GlobalPolicyDevicePostureRegularItem[]
  >;
};

export type GlobalPolicyDevicePostureWifiSettings = {
  [osType in DevicePostureOsType]: GlobalPolicyDevicePostureWifiItem[];
};

export type GlobalPolicyEmailSecurityApiResponse = EmailSecuritySettings & {
  mspEmailSecurityForwardingReportingSettingsDto: {
    adminEmail: string;
    blockedEmailReportsConfig: BlockedEmailReportsConfig;
    autoForwardingBlockingPolicy: AutoForwardingBlockingPolicyConfig;
  };
};

export type GlobalPolicyApiResponse = Omit<
  GlobalPolicyConfig,
  GlobalPolicySection.EMAIL_SECURITY
> & {
  [GlobalPolicySection.EMAIL_SECURITY]?: GlobalPolicyEmailSecurityApiResponse;
};

export type EndpointSecurityGlobalPostureSettings = GlobalPolicyRegularDevicePostureSettings & {
  usbLockdownSettings: GlobalPolicyDevicePostureUsbLockdownItem[];
  deviceWiFiSettings: GlobalPolicyDevicePostureWifiSettings;
  userAccountPolicy: GlobalPolicyDevicePostureUserAccountItem[];
  malwareScansScheduleSettings: MalwareScanItem[];
};

export interface GlobalPolicyConfig {
  name: string | null;
  description: string | null;
  id?: string;
  [GlobalPolicySection.CLOUD_SECURITY]?: CloudAppsSettings;
  [GlobalPolicySection.DEVICE_SETTINGS]?: DevicesSettings;
  [GlobalPolicySection.ENDPOINT_DATA_GOVERNANCE]?: PrivacySensitiveDataSettings;
  [GlobalPolicySection.USER_DATA_GOVERNANCE]?: UserDataGovernanceGlobalSettings;
  [GlobalPolicySection.EMAIL_SECURITY]?: EmailSecuritySettings;
  [GlobalPolicySection.ENDPOINT_SECURITY]?: EndpointSecurityGlobalSettings;
  [GlobalPolicySection.EDR]?: EdrSettings;
}

export const defaultGlobalPoliciesState = {
  globalPolicies: { items: [], total: 0 },
  pagination: { page: 0, pageSize: 25 },
  loading: false,
};

export interface GlobalPolicyWorkspaceToAssign {
  displayName: string;
  labels: MspLabel[];
  workspaceId: string;
  mspPolicyName?: string;
  type: WorkspaceType;
}

export interface ApplyPolicyFilters {
  policiesFilter: string[];
  labels: string[];
  search: string | null;
}

export interface ApplyGlobalPolicyData {
  policyId: string;
  workspaces: GlobalPolicyWorkspaceToAssign[];
}

export const defaultApplyPolicyFilters: ApplyPolicyFilters = {
  policiesFilter: [],
  labels: [],
  search: null,
};

export const useMspGlobalPoliciesStore = defineStore(StoreModule.MSP_GLOBAL_POLICIES, {
  state: (): GlobalPoliciesState => ({ ...defaultGlobalPoliciesState }),
  actions: {
    resetPagination() {
      this.pagination = { ...defaultGlobalPoliciesState.pagination };
    },
    async getGlobalPolicies() {
      const filters = useFiltersStore().filters[FilterContext.MSP_GLOBAL_POLICIES];
      const request = {
        ...api.mspGlobalPolicy(),
        params: {
          ...this.pagination,
          ...filters,
        },
      };
      this.loading = true;
      try {
        const { data } = await axiosInstance.request(request);
        this.globalPolicies = data;
      } catch (e) {
        console.log(e);
      } finally {
        this.loading = false;
      }
    },
    createGlobalPolicy(item: GlobalPolicyConfig) {
      const request = {
        ...api.mspGlobalPolicy(),
        method: "POST",
        data: {
          name: item.name,
          description: item.description,
        },
      };

      return axiosInstance.request(request);
    },
    updateCloudSecurity(data: Partial<CloudAppsSettings>, id: string) {
      const request = {
        ...api.mspCloudSecurityPolicy(id),
        method: "PUT",
        data,
      };

      return axiosInstance.request(request);
    },
    updateGeneralInfo(payload: GlobalPolicyConfig) {
      const request = {
        ...api.mspGeneralInfoPolicy(payload.id!),
        method: "PUT",
        data: {
          name: payload.name,
          description: payload?.description,
        },
      };

      return axiosInstance.request(request);
    },
    async updateDeviceSettings(data: DevicesSettings, id: string) {
      const normalizedDevicesSettings = await normalizeDevicesSettings(data);
      const request = {
        ...api.mspDeviceSettingsPolicy(id),
        method: "PUT",
        data: normalizedDevicesSettings,
      };

      return axiosInstance.request(request);
    },
    updateEndpointDataGovernanceSettings(data: PrivacySensitiveDataSettings, id: string) {
      const request = {
        ...api.mspEndpointDataGovernanceMonitoring(id),
        method: "PUT",
        data,
      };

      return axiosInstance.request(request);
    },
    updateUserDataGovernanceSettings(data: UserDataGovernanceGlobalSettings, id: string) {
      const monitoringRequest = {
        ...api.mspUserDataGovernanceMonitoring(id),
        method: "PUT",
        data: data.monitoring,
      };

      const exclusionsRequest = {
        ...api.mspUserDataGovernanceExclusions(id),
        method: "PUT",
        data: data.exclusions,
      };

      return Promise.all([
        axiosInstance.request(monitoringRequest),
        axiosInstance.request(exclusionsRequest),
      ]);
    },
    updateEmailSecuritySettings(data: EmailSecuritySettings, id: string) {
      const request = {
        ...api.mspEmailSecurityPolicy(id),
        method: "PUT",
        data: normalizeEmailSecuritySettings(data),
      };

      return axiosInstance.request(request);
    },
    updateEdrSettings(data: EdrSettings, id: string) {
      const request = {
        ...api.mspEndpointSecurityEdr(id),
        method: "PUT",
        data,
      };

      return axiosInstance.request(request);
    },
    async updateEndpointSecuritySettings(data: EndpointSecurityGlobalSettings, id: string) {
      const normalizedNgavSettings = await normalizeEndpointSecurityNgavSettings(data.ngav);
      const normalizedAddonsSettings = await normalizeEndpointSecurityAddonsSettings(data.addons);
      const ngavRequest = {
        ...api.mspEndpointSecurity(id),
        method: "PUT",
        data: normalizedNgavSettings,
      };

      const addonsRequest = {
        ...api.mspEndpointSecurityAddons(id),
        method: "PUT",
        data: normalizedAddonsSettings,
      };

      const postureRequest = {
        ...api.mspEndpointSecurityDevicePosture(id),
        method: "PUT",
        data: normalizePostureSettings(data.devicePosture),
      };

      return Promise.all([
        axiosInstance.request(ngavRequest),
        axiosInstance.request(addonsRequest),
        axiosInstance.request(postureRequest),
      ]);
    },
    async getGlobalPolicyById(id: string): Promise<AxiosResponse<GlobalPolicyConfig>> {
      const request = {
        ...api.getMspGlobalPolicy(id),
        method: "GET",
      };

      try {
        const response = await axiosInstance.request<GlobalPolicyApiResponse>(request);
        return {
          ...response,
          data: denormalizeGlobalPolicyResponse(response.data),
        };
      } catch (e) {
        console.error(e);
        useSnackbarStore().add({
          html: i18n.global.t("snackbar.messages.general.oops"),
          type: SnackbarTypes.ERROR,
        });
        throw e;
      }
    },
    async deleteGlobalPolicy(id: string): Promise<void> {
      this.loading = true;
      const request = {
        ...api.getMspGlobalPolicy(id),
        method: "DELETE",
      };
      try {
        await axiosInstance.request(request);
        useSnackbarStore().addGenericSuccess(
          i18n.global.t("snackbar.messages.globalMspPolicies.policyDeleted")
        );
        await this.getGlobalPolicies();
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    deleteUserDataGovernanceSection(id: string) {
      const request = {
        ...api.mspUserDataGovernance(id),
        method: "DELETE",
      };

      return axiosInstance.request(request);
    },
    deleteEndpointDataGovernanceSection(id: string) {
      const request = {
        ...api.mspEndpointDataGovernance(id),
        method: "DELETE",
      };

      return axiosInstance.request(request);
    },
    deleteDevicesSection(id: string) {
      const request = {
        ...api.mspDeviceSettingsPolicy(id),
        method: "DELETE",
      };

      return axiosInstance.request(request);
    },
    deleteCloudSecuritySection(id: string) {
      const request = {
        ...api.mspCloudSecurityPolicy(id),
        method: "DELETE",
      };

      return axiosInstance.request(request);
    },
    deleteEmailSecuritySection(id: string) {
      const request = {
        ...api.mspEmailSecurityPolicy(id),
        method: "DELETE",
      };

      return axiosInstance.request(request);
    },
    deleteEndpointSecuritySection(id: string) {
      const request = {
        ...api.mspEndpointSecurity(id),
        method: "DELETE",
      };

      return axiosInstance.request(request);
    },
    deleteEdrSection(id: string) {
      const request = {
        ...api.mspEndpointSecurityEdr(id),
        method: "DELETE",
      };

      return axiosInstance.request(request);
    },
    async getWorkspacesToAssignPolicy(
      payload: {
        filters: ApplyPolicyFilters;
        pagination: Pagination;
        assignedMspPolicyId: string;
      } = {
        filters: { ...defaultApplyPolicyFilters },
        pagination: { page: 0, pageSize: 10 },
        assignedMspPolicyId: "",
      }
    ) {
      const request = {
        ...api.getWorkspacesToAssignPolicy(
          payload.filters,
          payload.pagination,
          payload.assignedMspPolicyId
        ),
      };

      return axiosInstance.request(request);
    },
    async searchGlobalPolicies(
      search: string,
      pagination: Pagination = { page: 0, pageSize: 25 }
    ): Promise<AxiosResponse<Paged<GlobalPolicyListItem>>> {
      const request = {
        ...api.mspGlobalPolicy(),
        params: {
          search,
          ...pagination,
        },
      };

      return axiosInstance.request(request);
    },
    async applyGlobalPolicy(payload: ApplyGlobalPolicyData) {
      if (!payload.workspaces.length) return;
      const request = {
        ...api.assignGlobalPolicy,
        data: {
          policyId: payload.policyId,
          workspaceIds: payload.workspaces.map((w) => w.workspaceId),
        },
      };

      try {
        await axiosInstance.request(request);
        await this.getGlobalPolicies();
        useSnackbarStore().addGenericSuccess(
          i18n.global.t("snackbar.messages.globalMspPolicies.policyApplied")
        );
      } catch (e) {
        console.error(e);
      }
    },
  },
});
